import React, { useState, useEffect, useCallback } from 'react';
import api from '../utils/api';
import { toast } from 'sonner';
import DOMPurify from 'dompurify';
import {
  Mail, Inbox, Send, RefreshCw, Search, ChevronRight, ArrowLeft,
  Paperclip, Clock, User, X, AlertCircle, MailOpen, CheckCheck
} from 'lucide-react';

export default function GestionEmail() {
  const [mailboxes, setMailboxes] = useState([]);
  const [totalUnread, setTotalUnread] = useState(0);
  const [loadingMailboxes, setLoadingMailboxes] = useState(true);
  const [search, setSearch] = useState('');

  // Selected mailbox
  const [selectedMailbox, setSelectedMailbox] = useState(null);
  const [emails, setEmails] = useState([]);
  const [loadingEmails, setLoadingEmails] = useState(false);

  // Selected email
  const [selectedEmail, setSelectedEmail] = useState(null);
  const [loadingEmail, setLoadingEmail] = useState(false);

  // Reply
  const [showReply, setShowReply] = useState(false);
  const [markingAllRead, setMarkingAllRead] = useState(false);

  const fetchMailboxes = useCallback(async () => {
    setLoadingMailboxes(true);
    try {
      const res = await api.get('/emails/mailboxes');
      setMailboxes(res.data.mailboxes || []);
      setTotalUnread(res.data.total_unread || 0);
    } catch {
      toast.error('Erreur lors du chargement des boîtes mail');
    } finally {
      setLoadingMailboxes(false);
    }
  }, []);

  useEffect(() => {
    fetchMailboxes();
  }, [fetchMailboxes]);

  const selectMailbox = async (mb) => {
    setSelectedMailbox(mb);
    setSelectedEmail(null);
    setShowReply(false);
    setLoadingEmails(true);
    try {
      const res = await api.get(`/emails/client/${mb.client_id}/inbox?limit=30`);
      setEmails(res.data.emails || []);
    } catch (e) {
      const detail = e.response?.data?.detail;
      const msg = typeof detail === 'object' ? detail.message : (detail || 'Erreur de connexion');
      toast.error(msg);
      setEmails([]);
    } finally {
      setLoadingEmails(false);
    }
  };

  const openEmail = async (uid) => {
    if (!selectedMailbox) return;
    setLoadingEmail(true);
    setShowReply(false);
    try {
      const res = await api.get(`/emails/client/${selectedMailbox.client_id}/email/${uid}`);
      setSelectedEmail(res.data);
      // Refresh email list to update read status
      const listRes = await api.get(`/emails/client/${selectedMailbox.client_id}/inbox?limit=30`);
      setEmails(listRes.data.emails || []);
      // Update unread count in mailboxes list
      setMailboxes(prev => prev.map(mb =>
        mb.client_id === selectedMailbox.client_id
          ? { ...mb, unread_count: listRes.data.unread_count || 0 }
          : mb
      ));
      setTotalUnread(prev => {
        const diff = (selectedMailbox.unread_count || 0) - (listRes.data.unread_count || 0);
        return Math.max(0, prev - diff);
      });
      setSelectedMailbox(prev => ({ ...prev, unread_count: listRes.data.unread_count || 0 }));
    } catch {
      toast.error("Erreur lors de l'ouverture de l'email");
    } finally {
      setLoadingEmail(false);
    }
  };

  const refreshCurrentMailbox = async () => {
    if (!selectedMailbox) return;
    await selectMailbox(selectedMailbox);
  };

  const markAllRead = async () => {
    if (!selectedMailbox) return;
    setMarkingAllRead(true);
    try {
      const res = await api.post(`/emails/client/${selectedMailbox.client_id}/mark-all-read`);
      const count = res.data.marked_count || 0;
      toast.success(`${count} email${count > 1 ? 's' : ''} marqué${count > 1 ? 's' : ''} comme lu${count > 1 ? 's' : ''}`);
      // Refresh email list
      const listRes = await api.get(`/emails/client/${selectedMailbox.client_id}/inbox?limit=30`);
      setEmails(listRes.data.emails || []);
      // Update unread counts
      const prevUnread = selectedMailbox.unread_count || 0;
      setMailboxes(prev => prev.map(mb =>
        mb.client_id === selectedMailbox.client_id ? { ...mb, unread_count: 0 } : mb
      ));
      setSelectedMailbox(prev => ({ ...prev, unread_count: 0 }));
      setTotalUnread(prev => Math.max(0, prev - prevUnread));
    } catch {
      toast.error('Erreur lors du marquage');
    } finally {
      setMarkingAllRead(false);
    }
  };

  const filteredMailboxes = mailboxes.filter(mb => {
    if (!search) return true;
    const q = search.toLowerCase();
    return (mb.societe?.toLowerCase().includes(q) || mb.email?.toLowerCase().includes(q));
  });

  return (
    <div className="flex flex-col h-[calc(100vh-57px)]" data-testid="gestion-email-page">
      {/* Header */}
      <div className="flex items-center justify-between px-5 py-3 border-b border-[hsl(214,32%,91%)] bg-white flex-shrink-0">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 bg-[hsl(222,47%,11%)] rounded-lg flex items-center justify-center">
            <Mail size={18} className="text-white" />
          </div>
          <div>
            <h1 className="text-lg font-bold text-gray-900">Messagerie</h1>
            <p className="text-xs text-gray-500">{mailboxes.length} boîte{mailboxes.length > 1 ? 's' : ''} mail — {totalUnread} non lu{totalUnread > 1 ? 's' : ''}</p>
          </div>
        </div>
        <button
          onClick={fetchMailboxes}
          disabled={loadingMailboxes}
          className="flex items-center gap-2 px-3 py-2 text-sm text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
          data-testid="refresh-all-mailboxes"
        >
          <RefreshCw size={14} className={loadingMailboxes ? 'animate-spin' : ''} />
          Actualiser
        </button>
      </div>

      {/* 3-column layout */}
      <div className="flex flex-1 min-h-0 overflow-hidden">
        {/* Column 1: Mailboxes */}
        <div className="w-64 flex-shrink-0 border-r border-[hsl(214,32%,91%)] bg-white flex flex-col" data-testid="mailbox-list-column">
          {/* Search */}
          <div className="p-3 border-b border-gray-100">
            <div className="relative">
              <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder="Rechercher..."
                value={search}
                onChange={e => setSearch(e.target.value)}
                className="w-full pl-8 pr-3 py-1.5 border border-gray-200 rounded-lg text-xs focus:ring-2 focus:ring-gray-900/10 focus:border-gray-300 outline-none"
                data-testid="search-mailbox"
              />
            </div>
          </div>

          {/* Mailbox list */}
          <div className="flex-1 overflow-y-auto">
            {loadingMailboxes ? (
              <div className="flex items-center justify-center py-12">
                <RefreshCw size={20} className="animate-spin text-gray-400" />
              </div>
            ) : filteredMailboxes.length === 0 ? (
              <div className="px-4 py-8 text-center text-gray-400 text-xs">
                Aucune boîte mail trouvée
              </div>
            ) : (
              filteredMailboxes.map(mb => (
                <button
                  key={mb.client_id}
                  onClick={() => selectMailbox(mb)}
                  className={`w-full px-3 py-2.5 text-left border-b border-gray-50 transition-colors hover:bg-gray-50 ${
                    selectedMailbox?.client_id === mb.client_id ? 'bg-blue-50 border-l-2 border-l-blue-500' : ''
                  }`}
                  data-testid={`mailbox-item-${mb.client_id}`}
                >
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-sm font-medium text-gray-900 truncate">{mb.societe}</span>
                    {mb.unread_count > 0 && (
                      <span className="flex-shrink-0 bg-blue-500 text-white text-[10px] font-bold rounded-full w-5 h-5 flex items-center justify-center" data-testid={`unread-badge-${mb.client_id}`}>
                        {mb.unread_count > 9 ? '9+' : mb.unread_count}
                      </span>
                    )}
                  </div>
                  <p className="text-[11px] text-gray-500 truncate mt-0.5">{mb.email}</p>
                  {mb.status === 'erreur_auth' && (
                    <span className="inline-flex items-center gap-1 text-[10px] text-red-500 mt-0.5">
                      <AlertCircle size={10} /> Erreur auth
                    </span>
                  )}
                </button>
              ))
            )}
          </div>
        </div>

        {/* Column 2: Email list */}
        <div className="w-80 flex-shrink-0 border-r border-[hsl(214,32%,91%)] bg-white flex flex-col" data-testid="email-list-column">
          {selectedMailbox ? (
            <>
              {/* Mailbox header */}
              <div className="px-3 py-2.5 border-b border-gray-100 bg-gray-50 flex items-center justify-between flex-shrink-0">
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-gray-900 truncate">{selectedMailbox.societe}</p>
                  <p className="text-[11px] text-gray-500 truncate">{selectedMailbox.email}</p>
                </div>
                <div className="flex items-center gap-1 flex-shrink-0">
                  {selectedMailbox.unread_count > 0 && (
                    <button
                      onClick={markAllRead}
                      disabled={markingAllRead}
                      className="flex items-center gap-1 px-2 py-1 text-[11px] font-medium text-blue-600 hover:bg-blue-50 rounded-lg transition-colors disabled:opacity-50"
                      title="Tout marquer comme lu"
                      data-testid="mark-all-read-btn"
                    >
                      <CheckCheck size={13} className={markingAllRead ? 'animate-pulse' : ''} />
                      Tout lu
                    </button>
                  )}
                  <button
                    onClick={refreshCurrentMailbox}
                    disabled={loadingEmails}
                    className="p-1.5 hover:bg-gray-200 rounded-lg transition-colors"
                    data-testid="refresh-mailbox-emails"
                  >
                    <RefreshCw size={14} className={loadingEmails ? 'animate-spin' : ''} />
                  </button>
                </div>
              </div>

              {/* Email list */}
              <div className="flex-1 overflow-y-auto">
                {loadingEmails ? (
                  <div className="flex items-center justify-center py-12">
                    <RefreshCw size={20} className="animate-spin text-gray-400" />
                  </div>
                ) : emails.length === 0 ? (
                  <div className="flex flex-col items-center justify-center py-12 text-gray-400">
                    <Inbox size={32} className="mb-2 opacity-50" />
                    <p className="text-xs">Aucun email</p>
                  </div>
                ) : (
                  emails.map(em => {
                    const isUnread = !em.is_read;
                    const date = em.date ? new Date(em.date) : null;
                    const isSelected = selectedEmail?.uid === em.uid;
                    return (
                      <button
                        key={em.uid}
                        onClick={() => openEmail(em.uid)}
                        disabled={loadingEmail}
                        className={`w-full px-3 py-2.5 text-left border-b border-gray-50 transition-colors hover:bg-gray-50 ${
                          isSelected ? 'bg-blue-50 border-l-2 border-l-blue-500' : ''
                        } ${isUnread ? 'bg-blue-50/30' : ''}`}
                        data-testid={`email-item-${em.uid}`}
                      >
                        <div className="flex items-center gap-2">
                          {isUnread && <span className="w-2 h-2 bg-blue-500 rounded-full flex-shrink-0" />}
                          <span className={`text-xs truncate flex-1 ${isUnread ? 'font-semibold text-gray-900' : 'text-gray-600'}`}>
                            {em.from?.split('<')[0]?.trim() || em.from || 'Inconnu'}
                          </span>
                          {date && (
                            <span className="text-[10px] text-gray-400 flex-shrink-0">
                              {date.toLocaleDateString('fr-FR', { day: '2-digit', month: 'short' })}
                            </span>
                          )}
                        </div>
                        <p className={`text-xs truncate mt-0.5 ${isUnread ? 'font-medium text-gray-800' : 'text-gray-600'}`}>
                          {em.subject || '(Sans objet)'}
                        </p>
                        <p className="text-[10px] text-gray-400 truncate mt-0.5">
                          {em.body_text?.substring(0, 60) || ''}
                        </p>
                        {em.has_attachments && (
                          <Paperclip size={10} className="text-gray-400 mt-1" />
                        )}
                      </button>
                    );
                  })
                )}
              </div>
            </>
          ) : (
            <div className="flex flex-col items-center justify-center h-full text-gray-400">
              <MailOpen size={36} className="mb-3 opacity-40" />
              <p className="text-sm">Sélectionnez une boîte mail</p>
            </div>
          )}
        </div>

        {/* Column 3: Email content */}
        <div className="flex-1 bg-white flex flex-col min-w-0" data-testid="email-content-column">
          {selectedEmail ? (
            <EmailContent
              email={selectedEmail}
              clientId={selectedMailbox?.client_id}
              fromAddress={selectedMailbox?.email}
              showReply={showReply}
              onToggleReply={() => setShowReply(!showReply)}
              onReplySent={() => {
                setShowReply(false);
                refreshCurrentMailbox();
              }}
            />
          ) : loadingEmail ? (
            <div className="flex items-center justify-center h-full">
              <RefreshCw size={24} className="animate-spin text-gray-400" />
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center h-full text-gray-400">
              <Mail size={40} className="mb-3 opacity-30" />
              <p className="text-sm">Sélectionnez un email pour le lire</p>
              <p className="text-xs mt-1 opacity-70">L'email sera automatiquement marqué comme lu</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function EmailContent({ email, clientId, fromAddress, showReply, onToggleReply, onReplySent }) {
  const date = email.date ? new Date(email.date) : null;

  return (
    <div className="flex flex-col h-full">
      {/* Email header */}
      <div className="px-5 py-4 border-b border-gray-100 flex-shrink-0">
        <h2 className="text-lg font-semibold text-gray-900 leading-tight" data-testid="email-subject">
          {email.subject || '(Sans objet)'}
        </h2>
        <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-2 text-sm text-gray-500">
          <span className="flex items-center gap-1.5">
            <User size={14} />
            <span className="text-gray-700 font-medium">{email.from}</span>
          </span>
          {date && (
            <span className="flex items-center gap-1.5">
              <Clock size={14} />
              {date.toLocaleString('fr-FR', { day: '2-digit', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
            </span>
          )}
        </div>
        {email.to && (
          <p className="text-xs text-gray-400 mt-1">
            À : {email.to}
          </p>
        )}
      </div>

      {/* Email body */}
      <div className="flex-1 overflow-y-auto px-5 py-4">
        {email.body_html ? (
          <div
            className="prose prose-sm max-w-none"
            dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(email.body_html) }}
          />
        ) : (
          <pre className="whitespace-pre-wrap text-sm text-gray-700 font-sans leading-relaxed">
            {email.body_text || 'Contenu vide'}
          </pre>
        )}

        {/* Attachments */}
        {email.attachments?.length > 0 && (
          <div className="mt-4 p-3 bg-gray-50 rounded-lg border border-gray-100">
            <p className="text-xs text-gray-500 mb-2 flex items-center gap-1.5 font-medium">
              <Paperclip size={12} />
              {email.attachments.length} pièce{email.attachments.length > 1 ? 's' : ''} jointe{email.attachments.length > 1 ? 's' : ''}
            </p>
            <div className="flex flex-wrap gap-2">
              {email.attachments.map((att, i) => (
                <span key={`att-${att.filename}-${i}`} className="px-2.5 py-1 bg-white border border-gray-200 rounded-md text-xs text-gray-600">
                  {att.filename}
                </span>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Reply action bar */}
      <div className="px-5 py-3 border-t border-gray-100 bg-gray-50 flex-shrink-0">
        {!showReply ? (
          <button
            onClick={onToggleReply}
            className="flex items-center gap-2 px-4 py-2 bg-[hsl(222,47%,11%)] text-white rounded-lg text-sm hover:bg-[hsl(222,47%,18%)] transition-colors"
            data-testid="reply-email-btn"
          >
            <Send size={14} />
            Répondre
          </button>
        ) : (
          <ReplyForm
            clientId={clientId}
            fromAddress={fromAddress}
            replyTo={email.from?.match(/<(.+)>/)?.[1] || email.from}
            replySubject={email.subject?.startsWith('Re:') ? email.subject : `Re: ${email.subject}`}
            onCancel={onToggleReply}
            onSent={onReplySent}
          />
        )}
      </div>
    </div>
  );
}

function ReplyForm({ clientId, fromAddress, replyTo, replySubject, onCancel, onSent }) {
  const [body, setBody] = useState('');
  const [sending, setSending] = useState(false);

  const handleSend = async () => {
    if (!body.trim()) {
      toast.error('Veuillez écrire un message');
      return;
    }
    setSending(true);
    try {
      const res = await api.post(`/emails/client/${clientId}/send`, {
        to: replyTo,
        subject: replySubject,
        body,
        html: false,
      });
      if (res.data.success) {
        toast.success('Réponse envoyée !');
        onSent();
      } else {
        toast.error(res.data.error || "Erreur lors de l'envoi");
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur lors de l'envoi");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <p className="text-xs text-gray-500">
          Répondre à <span className="font-medium text-gray-700">{replyTo}</span>
        </p>
        <button onClick={onCancel} className="p-1 hover:bg-gray-200 rounded" data-testid="cancel-reply-btn">
          <X size={14} className="text-gray-500" />
        </button>
      </div>
      <textarea
        value={body}
        onChange={e => setBody(e.target.value)}
        placeholder="Votre réponse..."
        rows={4}
        className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[hsl(222,47%,11%)] resize-none"
        data-testid="reply-body-input"
        autoFocus
      />
      <div className="flex justify-end">
        <button
          onClick={handleSend}
          disabled={sending}
          className="flex items-center gap-2 px-4 py-2 bg-[hsl(222,47%,11%)] text-white rounded-lg text-sm hover:bg-[hsl(222,47%,18%)] disabled:opacity-50 transition-colors"
          data-testid="send-reply-btn"
        >
          {sending ? <RefreshCw size={14} className="animate-spin" /> : <Send size={14} />}
          {sending ? 'Envoi...' : 'Envoyer'}
        </button>
      </div>
    </div>
  );
}
